import { useEffect, useState } from "react";
import { Crown, Search, UserPlus, UserMinus, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, imgSrc } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const Avatar = ({ u }) => (u.picture
  ? <img src={imgSrc(u.picture)} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
  : <span className="w-10 h-10 rounded-full bg-rose-100 text-rose-600 font-bold grid place-items-center shrink-0">{(u.name || "?")[0]}</span>);
const date = (s) => (s ? new Date(s).toLocaleDateString("de-DE") : "–");

function Candidates({ onPick, reset }) {
  const [q, setQ] = useState("");
  useEffect(() => { setQ(""); }, [reset]);
  const [list, setList] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => { if (q.trim().length >= 2) api.get("/owner/admin-candidates", { params: { q } }).then(({ data }) => setList(data)).catch(() => {}); else setList([]); }, 300);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="space-y-2">
      <label className="relative block">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Mitglied nach Name oder E-Mail suchen …" data-testid="admin-team-search"
          className="w-full h-11 pl-11 pr-4 rounded-full border border-pink-100 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-rose-200" />
      </label>
      {list.map((u) => (
        <div key={u.user_id} className="flex items-center gap-3 p-3 rounded-2xl bg-white border border-pink-100" data-testid={`admin-candidate-${u.user_id}`}>
          <Avatar u={u} /><div className="min-w-0 flex-1"><p className="font-bold truncate">{u.name}</p><p className="text-xs text-slate-500 truncate">{u.email}{u.role === "moderator" ? " · Moderator" : ""}</p></div>
          <button onClick={() => onPick(u)} data-testid={`admin-appoint-${u.user_id}`} className="shrink-0 h-9 px-3 rounded-full bg-rose-500 text-white text-xs font-bold inline-flex items-center gap-1"><UserPlus className="w-4 h-4" /> Zum Admin ernennen</button>
        </div>
      ))}
    </div>
  );
}

export function AdminTeamAdmin() {
  const [admins, setAdmins] = useState(null);
  const [act, setAct] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reset, setReset] = useState(0);
  const load = () => api.get("/owner/admins").then(({ data }) => setAdmins(data)).catch((e) => toast.error(formatErr(e)));
  useEffect(() => { load(); }, []);
  const run = async () => {
    setBusy(true);
    try {
      if (act.add) await api.post(`/owner/admins/${act.u.user_id}`); else await api.delete(`/owner/admins/${act.u.user_id}`);
      toast.success(act.add ? `${act.u.name} ist jetzt Admin` : `${act.u.name} ist kein Admin mehr`);
      setAct(null); setReset((x) => x + 1); load();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-6" data-testid="admin-team-admin">
      <section className="space-y-2.5">
        <h3 className="font-extrabold">Aktuelle Admins</h3>
        {!admins ? <Loader2 className="w-5 h-5 animate-spin text-rose-400" /> : admins.map((u) => (
          <div key={u.user_id} className={`flex items-center gap-3 p-3 rounded-2xl border ${u.is_owner ? "bg-amber-50 border-amber-200" : "bg-white border-pink-100"}`} data-testid={`admin-row-${u.user_id}`}>
            <Avatar u={u} />
            <div className="min-w-0 flex-1">
              <p className="font-bold truncate flex items-center gap-1.5">{u.name}{u.is_owner && <Crown className="w-4 h-4 text-amber-500" />}</p>
              <p className="text-xs text-slate-500 break-words">{u.is_owner ? "Inhaber · steht über allen Admins" : `Admin seit ${date(u.appointed_at)} · ${u.admin_onboarded_at ? "Einführung abgeschlossen" : "Einführung noch offen"} · ${u.actions} Aktionen`}</p>
            </div>
            {u.is_owner ? <span className="shrink-0 text-[11px] font-bold text-amber-700 inline-flex items-center gap-1"><ShieldCheck className="w-4 h-4" /> geschützt</span>
              : <button onClick={() => setAct({ u, add: false })} data-testid={`admin-remove-${u.user_id}`} className="shrink-0 h-9 px-3 rounded-full bg-red-50 text-red-600 text-xs font-bold inline-flex items-center gap-1"><UserMinus className="w-4 h-4" /> Entfernen</button>}
          </div>
        ))}
      </section>
      <section className="space-y-2.5">
        <h3 className="font-extrabold">Neuen Admin ernennen</h3>
        <Candidates reset={reset} onPick={(u) => setAct({ u, add: true })} />
      </section>
      <AlertDialog open={!!act} onOpenChange={(o) => !o && setAct(null)}>
        <AlertDialogContent data-testid="admin-team-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{act?.add ? `${act?.u.name} zum Admin ernennen?` : `${act?.u.name} als Admin entfernen?`}</AlertDialogTitle>
            <AlertDialogDescription>{act?.add
              ? "Admins können fast alles in der App verändern: Punkte, Spiele, Mitglieder, Prämien, Texte und Push-Nachrichten an alle. Nur Admins ernennen und dein Konto verändern können sie nicht. Vor dem ersten Zugriff durchläuft der neue Admin eine Pflicht-Einführung. Ernenne nur Menschen, denen du voll vertraust."
              : "Das Mitglied verliert sofort alle Admin-Rechte und wird auf allen Geräten abgemeldet. Es kann sich danach ganz normal als Mitglied wieder anmelden."}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-team-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); run(); }} disabled={busy} data-testid="admin-team-confirm-btn" className={act?.add ? "bg-rose-500" : "bg-red-600"}>{act?.add ? "Ja, zum Admin ernennen" : "Ja, entfernen"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
